import * as React from "react";
import {
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Button,
    Pagination,
    Card,
    Badge,
} from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import DocumentWorkspaceViewer from "../../../../components/DocumentWorkspaceViewer/DocumentWorkspaceViewer";
import FilterButton from "../../../../components/FilterButton/FilterButton";
import {
    useFetchFormDocument,
    useGetClientForms,
} from "../../hooks/client-forms.queries";
import {
    formatDate,
    getROILink,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import {
    IClientForm,
    IFormDocument,
    FormType,
} from "../../types/client-forms.types";
import useCurrentClientProfile from "../../../../hooks/useCurrentClient";
import {
    getStatusDisplay,
    getStatusColors,
} from "../../helpers/client-forms.helpers";

const FORM_TYPE_OPTIONS = Object.values(FormType).map((type) => ({
    value: type,
    label: removeEnumUnderscore(type),
}));

export default function OtherForms() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentProfile = useCurrentClientProfile();
    const practiceName = currentProfile?.practice?.practice_name || "";

    const [showDocumentViewer, setShowDocumentViewer] = React.useState(false);
    const [selectedDocuments, setSelectedDocuments] = React.useState<
        IFormDocument[]
    >([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = React.useState(0);
    const selectedDocument = selectedDocuments[currentDocumentIndex];
    const {
        data: documentData,
        isLoading: isDocumentLoading,
        error: documentError,
    } = useFetchFormDocument(
        selectedDocument?.document_url ?? "",
        Boolean(selectedDocument?.document_url)
    );

    const startDateFromFilter = searchParams.get("start_date_from");
    const startDateToFilter = searchParams.get("start_date_to");
    const formTypeFilter = searchParams.get("form_type") || "";
    const pageFilter = Number(searchParams.get("other_forms_page")) || 1;
    const limitFilter = Number(searchParams.get("other_forms_imit")) || 10;

    const setParam = (key: string, value: string | null) => {
        if (!value) searchParams.delete(key);
        else searchParams.set(key, value);
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("other_forms_page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("other_forms_limit", String(limit));
        searchParams.set("other_forms_page", "1");
        setSearchParams(searchParams);
    };

    const { data, isSuccess } = useGetClientForms({
        form_type: (formTypeFilter as FormType) || undefined,
        start_date_from: startDateFromFilter || undefined,
        start_date_to: startDateToFilter || undefined,
        page: pageFilter,
        limit: limitFilter,
    });

    const handleViewDocuments = (documents: IFormDocument[]) => {
        if (documents.length > 0) {
            setSelectedDocuments(documents);
            setCurrentDocumentIndex(0);
            setShowDocumentViewer(true);
        }
    };

    const handleCloseDocumentViewer = () => {
        setShowDocumentViewer(false);
        setSelectedDocuments([]);
        setCurrentDocumentIndex(0);
    };

    return (
        <>
            <Dialog
                open={showDocumentViewer}
                onOpenChange={handleCloseDocumentViewer}
            >
                <DialogContent
                    variant="drawer"
                    title={
                        selectedDocuments[currentDocumentIndex]
                            ?.document_name || "Document"
                    }
                    handleCloseDialog={handleCloseDocumentViewer}
                    showFooter={false}
                    wrapperClass="w-[1180px] max-w-[calc(100vw-24px)]"
                    classNames="!overflow-hidden !p-0"
                >
                    <div>
                        {selectedDocument && (
                            <DocumentWorkspaceViewer
                                documentKey={selectedDocument.document_id}
                                data={documentData?.data}
                                contentType={
                                    documentData?.content_type ??
                                    selectedDocument.content_type
                                }
                                isLoading={isDocumentLoading}
                                isError={Boolean(documentError)}
                                title={selectedDocument.document_name}
                                navigation={{
                                    current: currentDocumentIndex + 1,
                                    total: selectedDocuments.length,
                                    onPrevious: () =>
                                        setCurrentDocumentIndex((previous) =>
                                            previous > 0
                                                ? previous - 1
                                                : selectedDocuments.length - 1
                                        ),
                                    onNext: () =>
                                        setCurrentDocumentIndex((previous) =>
                                            previous <
                                            selectedDocuments.length - 1
                                                ? previous + 1
                                                : 0
                                        ),
                                }}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <Card>
                <div className="flex justify-between items-center p-8 mb-12 flex-wrap gap-24">
                    <p className="text-base text-primary uppercase">
                        Other Forms
                    </p>
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton
                                text={
                                    formTypeFilter
                                        ? removeEnumUnderscore(formTypeFilter)
                                        : "Form Type"
                                }
                            />
                        </DropdownTrigger>
                        <DropdownContent
                            align="start"
                            width={220}
                            maxHeight={300}
                            className="overflow-y-auto"
                        >
                            <DropdownItem
                                isSelected={!formTypeFilter}
                                onClick={() => setParam("form_type", null)}
                            >
                                All Types
                            </DropdownItem>
                            {FORM_TYPE_OPTIONS.map((opt) => (
                                <DropdownItem
                                    key={opt.value}
                                    isSelected={formTypeFilter === opt.value}
                                    onClick={() =>
                                        setParam("form_type", opt.value)
                                    }
                                >
                                    {opt.label}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                </div>

                {data?.data && isSuccess && data.data.length > 0 && (
                    <>
                        <div className="flex flex-col gap-12">
                            {data.data.map((form: IClientForm) => {
                                const link =
                                    form.form_type ===
                                        "release_of_information" ||
                                    form.form_type === "roi_authorization"
                                        ? getROILink(practiceName)
                                        : null;
                                const authorizedUsers = form.form_content
                                    ?.authorized_users as string[];

                                return (
                                    <Card key={form.form_id} classNames="p-8">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-8 flex-wrap">
                                                    <p className="text-sm font-semibold text-black">
                                                        {makeStringFirstLetterCapital(
                                                            removeEnumUnderscore(
                                                                form.form_type
                                                            )
                                                        )}
                                                    </p>
                                                    {form.status && (
                                                        <Badge
                                                            content={getStatusDisplay(
                                                                form.status
                                                            )}
                                                            bgColor={
                                                                getStatusColors(
                                                                    form.status
                                                                ).bgColor
                                                            }
                                                            textColor={
                                                                getStatusColors(
                                                                    form.status
                                                                ).textColor
                                                            }
                                                        />
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-20 flex-wrap mt-10">
                                                    {authorizedUsers?.[0] && (
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-wide text-[rgba(11,19,43,0.4)] mb-1">
                                                                Authorized
                                                                Recipient
                                                            </p>
                                                            <p className="text-xs font-medium text-black">
                                                                {
                                                                    authorizedUsers[0]
                                                                }
                                                            </p>
                                                        </div>
                                                    )}
                                                    {form.start_date && (
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-wide text-[rgba(11,19,43,0.4)] mb-1">
                                                                Start Date
                                                            </p>
                                                            <p className="text-xs font-medium text-black">
                                                                {formatDate(
                                                                    form.start_date,
                                                                    false,
                                                                    "MM/dd/yyyy"
                                                                )}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {form.end_date && (
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-wide text-[rgba(11,19,43,0.4)] mb-1">
                                                                End Date
                                                            </p>
                                                            <p className="text-xs font-medium text-black">
                                                                {formatDate(
                                                                    form.end_date,
                                                                    false,
                                                                    "MM/dd/yyyy"
                                                                )}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {form.signed_at && (
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-wide text-[rgba(11,19,43,0.4)] mb-1">
                                                                Signed
                                                            </p>
                                                            <p className="text-xs font-medium text-black">
                                                                {formatDate(
                                                                    form.signed_at,
                                                                    false,
                                                                    "MM/dd/yyyy"
                                                                )}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-end gap-10 shrink-0">
                                                {form.document_links.length >
                                                    0 && (
                                                    <Button
                                                        variant="normal"
                                                        size="auto"
                                                        className="flex gap-1 p-0 font-normal text-primary text-xs"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewDocuments(
                                                                form.document_links
                                                            );
                                                        }}
                                                    >
                                                        <span>
                                                            {
                                                                form
                                                                    .document_links
                                                                    .length
                                                            }
                                                        </span>
                                                        document
                                                        {form.document_links
                                                            .length !== 1
                                                            ? "s"
                                                            : ""}
                                                    </Button>
                                                )}
                                                {link && (
                                                    <a
                                                        href={link}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-xs font-semibold text-primary underline underline-offset-2"
                                                    >
                                                        View form
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>

                        {data.total_count > limitFilter && (
                            <div className="mt-16">
                                <Pagination
                                    totalCount={data.total_count}
                                    totalPages={data.total_pages}
                                    currentPage={pageFilter}
                                    onPageChange={handlePageChange}
                                    limit={limitFilter}
                                    onLimitChange={handleLimitChange}
                                />
                            </div>
                        )}
                    </>
                )}
            </Card>
        </>
    );
}
